import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fullMockExamQuestions } from './data/fullMockExamQuestions';
import FullExamTimer from './components/FullExamTimer';
import FullExamNavigator from './components/FullExamNavigator';
import FullExamQuestionCard from './components/FullExamQuestionCard';
import FullExamResults from './components/FullExamResults';

type Phase = 'intro' | 'exam' | 'results' | 'review';

const FullMockExamPage = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<(string | null)[]>(Array(98).fill(null));
  const [secondsLeft, setSecondsLeft] = useState(134 * 60);
  const [timeUsed, setTimeUsed] = useState(0);
  const [flagged, setFlagged] = useState<boolean[]>(Array(98).fill(false));
  const [showSolution, setShowSolution] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  useEffect(() => {
    if (phase === 'exam' && secondsLeft > 0) {
      const timer = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 1) {
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [phase, secondsLeft]);

  const handleAutoSubmit = () => {
    const used = 134 * 60 - secondsLeft;
    setTimeUsed(used);
    setPhase('results');
  };

  const handleStartExam = () => {
    setPhase('exam');
    setCurrentIndex(0);
    setUserAnswers(Array(98).fill(null));
    setSecondsLeft(134 * 60);
    setTimeUsed(0);
    setFlagged(Array(98).fill(false));
    setShowSolution(false);
  };

  const handleAnswerSelect = (answer: string) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentIndex] = answer;
    setUserAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentIndex < 97) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleFlag = () => {
    const newFlagged = [...flagged];
    newFlagged[currentIndex] = !newFlagged[currentIndex];
    setFlagged(newFlagged);
  };

  const handleJumpToQuestion = (index: number) => {
    setCurrentIndex(index);
  };

  const handleSubmitClick = () => {
    setShowSubmitModal(true);
  };

  const handleConfirmSubmit = () => {
    const used = 134 * 60 - secondsLeft;
    setTimeUsed(used);
    setShowSubmitModal(false);
    setPhase('results');
  };

  const handleRetake = () => {
    setPhase('intro');
  };

  const handleReviewAnswers = () => {
    setPhase('review');
    setCurrentIndex(0);
    setShowSolution(true);
  };

  const handleBackToResults = () => {
    setPhase('results');
    setShowSolution(false);
  };

  const answeredCount = userAnswers.filter(a => a !== null).length;
  const unansweredCount = 98 - answeredCount;

  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/sat-prep')}
            className="flex items-center gap-2 text-slate-600 hover:text-indigo-600 mb-8 transition-colors"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-sm font-medium">Back to SAT Prep</span>
          </button>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-sky-500 p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <i className="ri-file-list-3-line text-3xl"></i>
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Complete SAT Mock Exam</h1>
                  <p className="text-indigo-100 text-sm mt-1">Full-length practice test with Math + Reading & Writing</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-6">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <i className="ri-question-line text-xl"></i>
                  <span className="font-semibold">98 Questions</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <i className="ri-timer-line text-xl"></i>
                  <span className="font-semibold">134 Minutes</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <i className="ri-book-open-line text-xl"></i>
                  <span className="font-semibold">2 Sections</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <i className="ri-focus-3-line text-xl"></i>
                  <span className="font-semibold">8 Domains</span>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="ri-layout-grid-line text-indigo-600"></i>
                  Exam Structure
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-200">
                    <h3 className="font-bold text-lg text-violet-900 mb-3 flex items-center gap-2">
                      <i className="ri-book-read-line"></i>
                      Reading & Writing
                    </h3>
                    <p className="text-sm text-violet-700 mb-4">54 questions · 64 minutes</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-violet-800">Information & Ideas</span>
                        <span className="font-semibold text-violet-900">14 Qs</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-violet-800">Craft & Structure</span>
                        <span className="font-semibold text-violet-900">14 Qs</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-violet-800">Expression of Ideas</span>
                        <span className="font-semibold text-violet-900">13 Qs</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-violet-800">Standard English Conventions</span>
                        <span className="font-semibold text-violet-900">13 Qs</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                    <h3 className="font-bold text-lg text-emerald-900 mb-3 flex items-center gap-2">
                      <i className="ri-calculator-line"></i>
                      Math
                    </h3>
                    <p className="text-sm text-emerald-700 mb-4">44 questions · 70 minutes</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-emerald-800">Algebra</span>
                        <span className="font-semibold text-emerald-900">11 Qs</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-emerald-800">Advanced Math</span>
                        <span className="font-semibold text-emerald-900">11 Qs</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-emerald-800">Problem Solving</span>
                        <span className="font-semibold text-emerald-900">11 Qs</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-emerald-800">Geometry & Trigonometry</span>
                        <span className="font-semibold text-emerald-900">11 Qs</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-indigo-600"></i>
                  Instructions
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-slate-700">
                    <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                    <span>You have <strong>134 minutes</strong> to complete all 98 questions across both sections</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-700">
                    <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                    <span>The timer will count down automatically and submit your exam when time expires</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-700">
                    <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                    <span>You can navigate between questions freely and flag questions for review</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-700">
                    <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                    <span>After submitting, you'll see your score breakdown by section and domain</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-700">
                    <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                    <span>Review mode will show step-by-step explanations for all questions</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleStartExam}
                className="w-full bg-gradient-to-r from-indigo-600 via-blue-600 to-sky-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap"
              >
                <i className="ri-play-circle-fill text-2xl"></i>
                Start Full Exam
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'results') {
    return (
      <FullExamResults
        userAnswers={userAnswers}
        questions={fullMockExamQuestions}
        timeUsed={timeUsed}
        onRetake={handleRetake}
        onReviewAnswers={handleReviewAnswers}
      />
    );
  }

  const currentQuestion = fullMockExamQuestions[currentIndex];
  const currentSection = currentIndex < 54 ? 'Reading & Writing' : 'Math';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <FullExamTimer secondsLeft={secondsLeft} />
              <div className="h-8 w-px bg-slate-300"></div>
              <div className="text-sm">
                <span className="font-semibold text-slate-800">{answeredCount}</span>
                <span className="text-slate-500"> / 98 answered</span>
              </div>
              <div className="h-8 w-px bg-slate-300"></div>
              <div className="text-sm font-medium text-slate-700">
                {currentSection}
              </div>
            </div>
            <button
              onClick={handleSubmitClick}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-check-double-line"></i>
              Submit Exam
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-6">
          <div className="flex-1">
            <FullExamQuestionCard
              question={currentQuestion}
              questionNumber={currentIndex + 1}
              userAnswer={userAnswers[currentIndex]}
              onAnswerSelect={handleAnswerSelect}
              showSolution={showSolution}
            />

            <div className="flex items-center justify-between mt-6">
              <button
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:border-indigo-500 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Previous
              </button>

              <button
                onClick={handleFlag}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  flagged[currentIndex]
                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                    : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-amber-500 hover:text-amber-600'
                }`}
              >
                <i className={flagged[currentIndex] ? 'ri-flag-fill' : 'ri-flag-line'}></i>
                {flagged[currentIndex] ? 'Flagged' : 'Flag for Review'}
              </button>

              <button
                onClick={handleNext}
                disabled={currentIndex === 97}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                Next
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {phase === 'review' && (
              <button
                onClick={handleBackToResults}
                className="w-full mt-6 bg-white border-2 border-slate-300 text-slate-700 py-3 rounded-lg font-semibold hover:border-indigo-500 hover:text-indigo-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Results
              </button>
            )}
          </div>

          <div className="w-80">
            <FullExamNavigator
              totalQuestions={98}
              currentIndex={currentIndex}
              userAnswers={userAnswers}
              flagged={flagged}
              onJumpToQuestion={handleJumpToQuestion}
            />
          </div>
        </div>
      </div>

      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-question-line text-3xl text-indigo-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-slate-800 text-center mb-2">Submit Exam?</h3>
            <p className="text-slate-600 text-center mb-6">
              You have answered <strong>{answeredCount}</strong> out of 98 questions.
              {unansweredCount > 0 && (
                <span className="block mt-2 text-amber-600 font-medium">
                  {unansweredCount} question{unansweredCount > 1 ? 's' : ''} remain unanswered.
                </span>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSubmitModal(false)}
                className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap"
              >
                Keep Working
              </button>
              <button
                onClick={handleConfirmSubmit}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
              >
                Submit Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FullMockExamPage;